"use client"

import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { CalendarPlus, ArrowRight, MapPin, Navigation } from 'lucide-react'
import { Link } from 'react-router-dom'
import { trackEvent } from '@/lib/analytics'
import { CTA } from '@/components/sections/cta'
import { CTA15Min } from '@/components/sections/cta-15min'

const getMapboxStaticImageUrl = (coordinates: string, width: number = 600, height: number = 300) => {
  const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
  const env = import.meta.env as { VITE_MAPBOX_TOKEN?: string; VITE_MAPBOX_ACCESS_TOKEN?: string }
  const accessToken = env.VITE_MAPBOX_TOKEN || env.VITE_MAPBOX_ACCESS_TOKEN
  
  if (!accessToken) {
    return ''
  }
  
  const styleId = 'streets-v12'
  const username = 'mapbox'
  const zoom = 14
  const pinColor = 'ff3333'
  const marker = `pin-l+${pinColor}(${lng},${lat})`
  
  return `https://api.mapbox.com/styles/v1/${username}/${styleId}/static/${marker}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${accessToken}`
}

const noosaAddress = '31 Burgess Drive, Tewantin QLD 4565'
const noosaCoordinates = '-26.406336265416424, 153.0262562704724'
const noosaPlusCode = 'H2VG+CG Tewantin, Queensland'
const noosaCid = 'c6ca6f5c24e27022'
const noosaPlaceId = 'ChIJEWpOLsNpk2sRInDiJFxvysY'

const maroochydoreAddress = 'Ground Level 5/55 Plaza Parade, Maroochydore QLD 4558'
const maroochydoreCoordinates = '-26.657529591547217, 153.08437335189674'

const openDirections = (address: string, coordinates?: string, plusCode?: string, cid?: string, placeId?: string) => {
  const encodedAddress = encodeURIComponent(address)
  
  let coords = ''
  if (coordinates) {
    const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
    coords = `${lat},${lng}`
  }
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  let mapUrl: string
  let googleMapsUrl: string
  
  if (isIOS) {
    mapUrl = coords 
      ? `maps://maps.apple.com/?daddr=${coords}&dirflg=d`
      : `maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`
    window.location.href = mapUrl
    
    if (placeId) {
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else if (cid) {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=cid:${cid}&travelmode=driving`
    } else if (plusCode) {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(plusCode)}&travelmode=driving`
    } else {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coords || encodedAddress}&travelmode=driving`
    }
    
    setTimeout(() => {
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  } else {
    if (placeId) {
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else if (cid) {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=cid:${cid}&travelmode=driving`
    } else if (plusCode) {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(plusCode)}&travelmode=driving`
    } else {
      googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coords || encodedAddress}&travelmode=driving`
    }
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
  }
}

export default function AboutPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-background">
      <Helmet>
        <title>About Raintree Chinese Medicine | Renee Knott (Chinese Med)</title>
        <meta name="description" content="Learn about Renee's approach to acupuncture and Chinese medicine, supporting women's health across the Sunshine Coast." />
        <meta property="og:title" content="About Raintree Chinese Medicine" />
        <meta property="og:description" content="Learn about Renee's approach to acupuncture and Chinese medicine, supporting women's health across the Sunshine Coast." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/about" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Raintree Chinese Medicine" />
        <meta name="twitter:description" content="Learn about Renee's approach to acupuncture and Chinese medicine, supporting women's health across the Sunshine Coast." />
      </Helmet>
      <h1 className="sr-only">About Raintree Chinese Medicine</h1>
      <BreadcrumbsJsonLd />
      
      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                About Raintree
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-4 text-sm sm:text-base font-medium">
                Raintree was created with one simple intention:<br />
                to slow things down.
              </p>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-4 text-sm sm:text-base">
                At Raintree, acupuncture is thoughtful and unhurried. Where care is personal, considered, and grounded in experience of the rich knowledge and wisdom of Chinese Medicine.
              </p>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Raintree offers acupuncture and Chinese herbal medicine for women's health, fertility, pregnancy and related concerns, with quiet, unhurried appointments in Noosa and Maroochydore.
              </p>
              <div className="flex flex-col gap-3 sm:w-2/3 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('about_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Initial consult and treatment
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('about_hero_followup')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Follow-up appointment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/10 sm:w-full mx-auto"
                  onClick={() => openBooking('about_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  About Raintree
                </h1>
                <p className="text-white/90 text-base lg:text-xl mb-4 font-medium block">
                  Raintree was created with one simple intention:<br />
                  to slow things down.
                </p>
                <p className="text-white/90 text-base lg:text-lg mb-4">
                  At Raintree, acupuncture is thoughtful and unhurried. Where care is personal, considered, and grounded in experience of the rich knowledge and wisdom of Chinese Medicine.
                </p>
                <p className="text-white/90 text-base lg:text-lg">
                  Raintree offers acupuncture and Chinese herbal medicine for women's health, fertility, pregnancy and related concerns, with quiet, unhurried appointments in Noosa and Maroochydore.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('about_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Initial consult and treatment
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('about_hero_followup')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Follow-up appointment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('about_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Raintree Exists Section */}
      <section className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Why Raintree Exists
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-6 text-left font-medium">
                Raintree grew out of many years of working in busy clinics where time was short and people often felt like they were moving through a system rather than being truly met.
              </p>
              <p className="text-gray-600 mb-6 text-left font-medium">
                The intention here is different:
              </p>
              <ul className="list-disc list-inside space-y-3 text-gray-600 mb-6 text-left">
                <li>To create a clinic where there is time to <strong>listen properly</strong></li>
                <li>To hold space for the emotional side of health as well as the physical</li>
                <li>To offer <strong>steady support over longer periods,</strong> not just one or two appointments</li>
                <li>To bring the depth of Chinese medicine into everyday life in a clear, grounded way</li>
              </ul>
              <p className="text-gray-600 text-left">
                It's a place for people who are looking for thoughtful, ongoing care – particularly around hormones, fertility, pregnancy, stress, sleep and digestion.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Care Works Here Section */}
      <section className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              How Care Works Here
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-6 text-left font-medium">
                Instead of trying to cover everything, Raintree focuses on a small number of connected areas:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                <li>Fertility and preconception care (with or without IVF)</li>
                <li>Pregnancy and postpartum support</li>
                <li>Menstrual and hormonal health</li>
                <li>Stress, sleep and digestive balance</li>
                <li>Ongoing women's health and long-term care</li>
              </ul>
              <p className="text-gray-600 mb-6 text-left">
                Within these areas, the same appointment types are used for everyone. What changes is <strong>how the treatment is tailored</strong> – taking into account your history, your current priorities, and what is realistic in your daily life.
              </p>
              <p className="text-gray-600 mb-6 text-left">
                While some concerns don't fit neatly into these categories, there is also space for more complex presentations. People often come to Raintree for concerns such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                <li>Tension-related pain</li>
                <li>Chronic headaches or migraines</li>
                <li>Complex symptom patterns</li>
                <li>Cancer care and support</li>
              </ul>
              <p className="text-gray-600 mb-6 text-left">
                Chinese medicine at Raintree always sits <strong>alongside</strong> the care you receive from your GP, specialist, midwife, IVF team or treating team. The aim is to support you, not to replace other parts of your healthcare.
              </p>
              <div className="mt-6">
                <Link
                  to="/what-we-help-with"
                  className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                >
                  Learn more about What We Help With
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What It Feels Like to Be Here Section - 2 column with image */}
      <section className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="md:hidden">
              <div className="flex justify-center mb-6">
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Clinic-01.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLUNsaW5pYy0wMS5wbmciLCJpYXQiOjE3NjIyOTg1MTAsImV4cCI6NDg4NDM2MjUxMH0.1nvCGNDy0JWPtQgOK01sXS3NrDSBO0rMKqTYRvT4Mfo"
                  alt="Peaceful clinic environment"
                  className="aspect-square object-cover w-full max-w-sm shadow-lg"
                />
              </div>
              <div className="pl-4 max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl mb-4 mt-8 text-[#3f4141] text-center">
                  What It Feels Like to Be Here
                </h2>
                <p className="text-gray-600 mb-4 text-center font-medium">
                  The clinic spaces are intentionally simple and quiet.
                </p>
                <p className="text-gray-600 mb-4 text-center font-medium">
                  Appointments are:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-center">
                  <li>Unhurried, with time to talk as well as to rest</li>
                  <li>Grounded in clear explanations and practical suggestions</li>
                  <li>Designed to give your nervous system a chance to settle</li>
                </ul>
                <p className="text-gray-600 mb-4 text-center">
                  Many people describe their sessions as a regular pause in a busy life – a place to exhale, be heard and recalibrate before stepping back into everything they hold.
                </p>
                <p className="text-gray-600 mb-6 text-center">
                  You are not expected to arrive with a neat story or a clear plan. Part of the work is <strong>making sense of things together</strong> over time.
                </p>
                <div className="text-center">
                  <Link
                    to="/appointments"
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                  >
                    Learn more about Appointments
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Desktop: 2-column layout - image left, text right */}
            <div className="hidden md:grid md:grid-cols-2 md:gap-8 lg:gap-12 md:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex items-center justify-center"
              >
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Clinic-01.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLUNsaW5pYy0wMS5wbmciLCJpYXQiOjE3NjIyOTg1MTAsImV4cCI6NDg4NDM2MjUxMH0.1nvCGNDy0JWPtQgOK01sXS3NrDSBO0rMKqTYRvT4Mfo"
                  alt="Peaceful clinic environment"
                  className="aspect-square md:aspect-auto object-cover w-full md:w-auto md:h-full md:max-h-[600px] shadow-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex flex-col justify-center"
              >
                <div className="max-w-xl">
                  <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-left">
                    What It Feels Like to Be Here
                  </h2>
                  <p className="text-gray-600 mb-4 text-left">
                    The clinic spaces are intentionally simple and quiet.
                  </p>
                  <p className="text-gray-600 mb-4 text-left font-medium">
                    Appointments are:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                    <li>Unhurried, with time to talk as well as to rest</li>
                    <li>Grounded in clear explanations and practical suggestions</li>
                    <li>Designed to give your nervous system a chance to settle</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    Many people describe their sessions as a regular pause in a busy life – a place to exhale, be heard and recalibrate before stepping back into everything they hold.
                  </p>
                  <p className="text-gray-600 mb-6 text-left">
                    You are not expected to arrive with a neat story or a clear plan. Part of the work is <strong>making sense of things together</strong> over time.
                  </p>
                  <div>
                    <Link
                      to="/appointments"
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                    >
                      Learn more about Appointments
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who You'll See Section - 2 column with image */}
      <section className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="md:hidden">
              <div className="flex justify-center mb-6">
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee-Knott.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLUtub3R0LnBuZyIsImlhdCI6MTc2MjI5ODM3NSwiZXhwIjo0ODg0MzYyMzc1fQ.oWb0fbHoIsvcA0Jc2IAok36HiwggiQ3bs_1iY-jpBXU"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist"
                  className="aspect-square object-cover w-full max-w-sm shadow-lg"
                />
              </div>
              <div className="pl-4 max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl mb-4 mt-8 text-[#3f4141] text-center">
                  Who You'll See
                </h2>
                <p className="text-gray-600 mb-6 text-center">
                  Raintree is owned and run by <strong>Dr Renee Knott (Chinese Med)</strong>, an AHPRA-registered acupuncturist and Chinese medicine herbalist with over two decades of clinical experience in women's health, fertility and pregnancy, and many other areas.
                </p>
                <p className="text-gray-600 mb-4 text-center font-medium">
                  Renee's work brings together:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-center">
                  <li>A deep respect for the classical roots of Chinese medicine</li>
                  <li>Many years working in busy fertility- and women's-health clinics</li>
                  <li>A calm, steady style that values clarity, collaboration and long-term relationships</li>
                </ul>
                <p className="text-gray-600 mb-6 text-center">
                  If you'd like to read more about her background, training and personal story, you can visit:
                </p>
                <div className="text-center">
                  <Link
                    to="/dr-renee-knott"
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                  >
                    Meet Dr Renee Knott
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Desktop: 2-column layout - text left, image right */}
            <div className="hidden md:grid md:grid-cols-2 md:gap-8 lg:gap-12 md:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex flex-col justify-center"
              >
                <div className="max-w-xl">
                  <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-left">
                    Who You'll See
                  </h2>
                  <p className="text-gray-600 mb-6 text-left">
                    Raintree is owned and run by <strong>Dr Renee Knott (Chinese Med)</strong>, an AHPRA-registered acupuncturist and Chinese medicine herbalist with over two decades of clinical experience in women's health, fertility and pregnancy, and many other areas.
                  </p>
                  <p className="text-gray-600 mb-4 text-left font-medium">
                    Renee's work brings together:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                    <li>A deep respect for the classical roots of Chinese medicine</li>
                    <li>Many years working in busy fertility- and women's-health clinics</li>
                    <li>A calm, steady style that values clarity, collaboration and long-term relationships</li>
                  </ul>
                  <p className="text-gray-600 mb-6 text-left">
                    If you'd like to read more about her background, training and personal story, you can visit:
                  </p>
                  <div>
                    <Link
                      to="/dr-renee-knott"
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                    >
                      Meet Dr Renee Knott
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex items-center justify-center"
              >
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee-Knott.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLUtub3R0LnBuZyIsImlhdCI6MTc2MjI5ODM3NSwiZXhwIjo0ODg0MzYyMzc1fQ.oWb0fbHoIsvcA0Jc2IAok36HiwggiQ3bs_1iY-jpBXU"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist"
                  className="aspect-square md:aspect-auto object-cover w-full md:w-auto md:h-full md:max-h-[600px] shadow-lg"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA 15mins Section */}
      <CTA15Min onBook={openBooking} />

      {/* Where You Can Find Us Section - Location Cards */}
      <section className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Where You Can Find Us
            </h2>
            <p className="text-gray-600 mb-6 text-center max-w-3xl mx-auto">
              Raintree currently has two clinic locations on the Sunshine Coast.<br />
              Choose whichever clinic is easiest to reach, and you can move between them if your circumstances change.
            </p>
            
            {/* Link to clinics page */}
            <div className="text-center mb-8">
              <Link
                to="/clinics"
                className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
              >
                Learn more about opening hours, directions, parking and access information
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            
            {/* Location Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-8">
              {/* Noosa/Tewantin Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col"
              >
                <div className="w-full h-48 overflow-hidden relative bg-gray-200">
                  {noosaCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(noosaCoordinates, 1200, 300)}
                      alt="Map showing location of Tewantin clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(noosaAddress, noosaCoordinates, noosaPlusCode, noosaCid, noosaPlaceId)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Tewantin clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-4 md:p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-medium text-[#3f4141] mb-2">Tewantin Clinic</h3>
                  <p className="text-gray-600 text-sm mb-4">Serving Noosa, Noosaville, Tewantin and surrounding areas</p>
                  <p className="text-gray-700 font-medium mb-4">{noosaAddress}</p>
                  <div className="mt-auto">
                    <Button
                      className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                      onClick={() => openBooking('about_noosa_clinic')}
                    >
                      <span className="inline-flex items-center gap-2">
                        <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                        Book Noosa Clinic
                      </span>
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* Maroochydore Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col"
              >
                <div className="w-full h-48 overflow-hidden relative bg-gray-200">
                  {maroochydoreCoordinates ? (
                    <img
                      src={getMapboxStaticImageUrl(maroochydoreCoordinates, 1200, 300)}
                      alt="Map showing location of Maroochydore clinic"
                      className="w-full h-full object-cover"
                      style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <MapPin className="h-8 w-8" />
                    </div>
                  )}
                  <button
                    onClick={() => openDirections(maroochydoreAddress, maroochydoreCoordinates)}
                    className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors z-10"
                    aria-label="Get directions to Maroochydore clinic"
                  >
                    <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Get Directions</span>
                  </button>
                </div>
                <div className="p-4 md:p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-medium text-[#3f4141] mb-2">Maroochydore Clinic</h3>
                  <p className="text-gray-600 text-sm mb-4">Centrally located for Maroochydore, Buderim, Bli Bli, Cotton Tree, Mooloolaba and nearby suburbs</p>
                  <p className="text-gray-700 font-medium mb-4">{maroochydoreAddress}</p>
                  <div className="mt-auto">
                    <Button
                      className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                      onClick={() => openBooking('about_maroochydore_clinic')}
                    >
                      <span className="inline-flex items-center gap-2">
                        <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                        Book Maroochydore Clinic
                      </span>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is Raintree the Right Fit for You Section */}
      <section className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Is Raintree the Right Fit for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-6 text-left">
                Raintree may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                <li>Prefer <strong>calm, steady care</strong> over quick fixes</li>
                <li>Are navigating complex or long-standing patterns around hormones, cycles, fertility, pregnancy, stress, sleep or digestion</li>
                <li>Would like someone to walk alongside you over time, rather than starting from scratch with a new practitioner every few months</li>
                <li>Want Chinese medicine that respects and works alongside your existing medical care</li>
              </ul>
              <p className="text-gray-600 mb-6 text-left font-medium">
                If you're not sure, you don't have to decide on everything at once.
              </p>
              <p className="text-gray-600 mb-4 text-left">
                You can:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-left">
                <li>Book an <strong>Initial Consultation & Treatment</strong> to begin, or</li>
                <li>Start with a <strong>free 15-minute phone consult</strong> to talk briefly about what you're going through and whether this style of care is likely to be helpful.</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Next Steps
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-6 text-left font-medium">
                If you'd like to explore working together:
              </p>
              <ul className="list-disc list-inside space-y-3 text-gray-600 mb-6 text-left">
                <li>
                  <Link
                    to="/what-we-help-with"
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                  >
                    Read more about <strong>what we help with</strong>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/appointments"
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                  >
                    View <strong>appointments and pricing</strong>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/clinics"
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors"
                  >
                    See <strong>clinic locations</strong>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => openBooking('about_next_steps')}
                    className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-left"
                  >
                    Or go straight to <strong>book online</strong> at the clinic that's easiest for you
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </li>
              </ul>
              <p className="text-gray-600 text-left">
                The hope is that, from the moment you first read about Raintree through to your time in the clinic, you feel met with clarity, warmth and genuine care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  )
}
